/**
 * Login Replacer View
 */

define(function(require, exports, module) {
    'use strict';

    const $ = require('j');
    const core = require('core');
    const user = require('user');
    const _ = require('underscore');
    const config = require('config');
    const server = require('server');
    const url = require('util/url.util');
    const Form = require('class/form.class');
    const BaseView = require('common/base.view');
    const storage = require('util/storage.util');
    const serverUtil = require('util/server.util');
    const recaptchaMixin = require('modules/google-recaptcha/mixin');

    module.exports = BaseView.extend({
        events: _.extend({}, BaseView.prototype.events, {
            'submit .fn-login': 'login'
        }),

        mixins: [recaptchaMixin],

        accessRules: {
            allow: ['guest']
        },

        defaults: {
            options: {
                isRefreshPage: true
            }
        },

        formTemplate: 'login.view',
        password: undefined,
        isRememberMeSupported: false,
        isRememberDeviceSupported: false,

        initialize: function(options) {
            this.options = _.extend({}, this.defaults.options, options);
            Playtech.API.auth.getTwoFactorConfiguration()
                .then(({ twoFactorConf = {} }) => {
                    this.isRememberDeviceSupported = twoFactorConf.loginDeviceSupported;
                    this.isRememberMeSupported = twoFactorConf.rememberMeWithoutTwoFactor === 'ON'
                        && !Playtech.API.htcmd.isEnabled && !Playtech.API.htcmd.isWrapperEnabled;

                    this.render();
                });

            this.initRecaptcha({
                selector: '.fn-login-recaptcha',
                operation: 'login'
            });

            const afterLoginUrl = url.getLocationParameterByName('afterLoginUrl');

            if (afterLoginUrl) {
                const cleanURL = new URL(window.location.href);

                cleanURL.searchParams.delete('afterLoginUrl');

                storage.session.setItem('afterLoginUrl', afterLoginUrl);
                window.Playtech.API.common.replace(window.location.pathname, undefined, {
                    removeParams: ['afterLoginUrl']
                });
            }
        },

        subscribe: function() {
            this.subscribeOnRecaptcha();
        },

        getTmplData: function() {
            return {
                validationConfig: {
                    userName: {
                        required: true
                    },
                    password: {
                        required: true
                    },
                    fitToPlay: {
                        required: Playtech.API.auth.getFitToPlaySubmissionRequired()
                    }
                }
            };
        },

        render: function() {
            this.$el.html(this.tmpl(this.formTemplate, {
                isFitToPlaySubmissionRequired: Playtech.API.auth.getFitToPlaySubmissionRequired(),
                isRememberMeSupported: this.isRememberMeSupported,
                isRememberDeviceSupported: this.isRememberDeviceSupported,
                upperCase: config.get('isUsernameInUppercase')
            }));

            this.form = new Form(this.$('.fn-login'), this.getTmplData());

            return this;
        },

        isEmailLoginMode: function(data) {
            var emailRegexp = /^[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/i;

            return config.get('usernameEmailSwitcher') && emailRegexp.test(data.userName);
        },

        /**
         * Send login request
         * @return {Deferred}
         */
        login: function(event) {
            if (event) {
                event.preventDefault();
            }

            const loginState = $.Deferred();
            const recaptchaError = this.getRecaptchaError();

            if (recaptchaError) {
                this.form.showErrorMessage(recaptchaError);
                loginState.reject();
            } else {
                this.form.validate()
                    .done(function(data, form) {
                        form.showLoading();

                        user.set(this.getLoginParams(data));

                        this.password = data.password;

                        user
                            .fetch()
                            .always(() => form.hideLoading())
                            .done(function() {
                                this.successLogin.apply(this, arguments);
                                loginState.resolve();
                            }.bind(this))
                            .fail(function(error) {
                                if (error?.data?.isPrevented) {
                                    // do not resolve or reject here
                                    // otherwise, native app will receive reset
                                    return;
                                }

                                this.errorLogin.apply(this, arguments);
                                loginState.reject(error);
                            }.bind(this));
                    }.bind(this))
                    .fail(function() {
                        this.errorLogin({
                            data: {
                                errorCode: 0
                            }
                        });
                    }.bind(this));
            }

            return loginState.promise();
        },

        getLoginParams: function(data) {
            var userName = config.get('isUsernameInUppercase') ? data.userName.toUpperCase() : data.userName;
            var params = {
                password: data.password,
                userName: userName,
                loginMode: this.isEmailLoginMode(data) ? 'email' : 'userName',
                afterRegisterLogin: false
            };

            if (this.form.el.elements.rememberMe) {
                params.rememberMeLogin = this.form.el.elements.rememberMe.checked;
            }

            if (this.form.el.elements.rememberDevice) {
                params.rememberDevice = this.form.el.elements.rememberDevice.checked;
            }

            if (this.form.el.elements.fitToPlay) {
                params.fitToPlay = this.form.el.elements.fitToPlay.checked;
            }

            if (this.form.el.elements.rememberDevice) {
                params.rememberDevice = this.form.el.elements.rememberDevice.checked;
            }

            return params;
        },

        /**
         * Success login action
         */
        successLogin: function() {
            this.hideKeyboard();

            user.storeRememberMeItem();

            if (this.options.isRefreshPage) {
                core.trigger('page:refresh');
            }
        },

        /**
         * Error login action
         * @param  {Object} response
         */
        errorLogin: function(response) {
            const { data } = response || {};
            const content = serverUtil.getMessageFromActions(response)
                || serverUtil.getErrorMessage(response, { prefix: 'playtech.mobile.system.login.error.' });

            if(this.form.data('userName')) {
                this.form.el.elements.password.focus();
            } else {
                this.form.el.elements.userName.focus();
            }

            if (!data) {
                return;
            }

            if (data.errorCode === 301) {
                this.showSubmitRecaptcha({
                    onError: err => this.form.showErrorMessage(err)
                });

                return;
            }

            if (data.errorCode === 0) {
                return;
            }

            this.form.showErrorMessage({ content });
        },

        close: function() {
            $(window).off('.login');

            BaseView.prototype.close.apply(this, arguments);
        },

        getIsLoginDeviceSupported: function() {
            server.send({
                ID: 35757,
                description: 'Get Two Factor Conf',
                responses: [35758],
                casinoName: config.get('casinoName')
            }).done((data) => {
                this.loginDeviceSupported = data.data.twoFactorConf.loginDeviceSupported;
                this.render();
            });
        }

    });
});
